import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, Search, X } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { cn } from "@/lib/utils";
import { useSession } from "@/session";

/**
 * La coquille applicative, responsive aux trois largeurs du plan du LOT 0 :
 * 390 px (terrain), 768–1024 px (tablette), ≥ 1280 px (bureau).
 *
 * Sous 1024 px la navigation devient un tiroir ; au-delà elle est une colonne
 * fixe. Il n'y a pas trois mises en page mais une seule, qui se replie — c'est
 * ce qui évite qu'une quatrième largeur soit un écran cassé.
 *
 * Les entrées sont des LIENS depuis le LOT 1, et non plus des boutons : une
 * adresse de Commande / Projet doit pouvoir se copier, s'ouvrir dans un onglet et revenir
 * par le bouton « Précédent ». Un bouton ne sait rien faire de tout cela.
 */

/**
 * Les quatre intertitres du menu (ticket #63, décisions 77, 81 et 107), dans
 * l'ordre où ils s'affichent. Une entrée se déclare sous l'un d'eux ; un
 * intertitre qu'aucune entrée visible ne porte ne s'affiche pas — aucune
 * rubrique vide, donc, et aucune à créer pour remplir.
 */
export const INTERTITRES = [
  "Navigation principale",
  "Approvisionnements",
  "Référentiels",
  "Administration et pilotage",
] as const;

export type Intertitre = (typeof INTERTITRES)[number];

export type Entree = {
  vers: string;
  libelle: string;
  icone: ReactNode;
  intertitre: Intertitre;
  exact?: boolean;
  /** Rangée sous l'entrée `parent` : « Lots » sous « Commandes / Projets ». */
  sous?: boolean;
  parent?: string;
};

/** Les entrées regroupées sous leur intertitre, dans l'ordre des intertitres. */
function rubriques(entrees: Entree[]) {
  return INTERTITRES.map((intertitre, rang) => ({
    intertitre,
    // Un identifiant ne porte pas d'espace : `aria-labelledby` le lirait en deux.
    id: `intertitre-${rang}`,
    entrees: entrees.filter((entree) => entree.intertitre === intertitre),
  })).filter((rubrique) => rubrique.entrees.length > 0);
}

export function Coquille({ entrees, children }: { entrees: Entree[]; children: ReactNode }) {
  const { deconnexion, ...session } = useSession();
  const [tiroirOuvert, setTiroirOuvert] = useState(false);
  const emplacement = useLocation();
  const identite = session.statut === "connecte" ? session.identite : null;

  // Changer de page referme le tiroir : sinon il masque l'écran qu'on vient
  // d'ouvrir, ce qui sur un téléphone donne l'impression que rien n'a bougé.
  useEffect(() => setTiroirOuvert(false), [emplacement.pathname]);

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/* Barre supérieure — visible sous 1024 px seulement. */}
      <header className="flex items-center gap-3 bg-anthracite px-4 py-3 lg:hidden">
        <button
          type="button"
          aria-label={tiroirOuvert ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={tiroirOuvert}
          onClick={() => setTiroirOuvert((ouvert) => !ouvert)}
          className="rounded-[6px] p-2 text-blanc-casse hover:bg-anthracite-clair"
        >
          {tiroirOuvert ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <Link to="/" className="text-lg font-semibold tracking-[0.15em] text-blanc-casse">
          LABGEST
        </Link>
      </header>

      {/* Navigation — tiroir sous 1024 px, colonne fixe au-delà. */}
      <nav
        aria-label="Navigation principale"
        className={cn(
          "bg-anthracite text-blanc-casse lg:flex lg:w-64 lg:shrink-0 lg:flex-col",
          tiroirOuvert ? "flex flex-col" : "hidden",
        )}
      >
        <Link to="/" className="hidden px-5 py-5 text-xl font-semibold tracking-[0.15em] lg:block">
          LABGEST
        </Link>

        <div className="px-3 pb-1 pt-3 lg:pt-0">
          <ChampRecherche />
        </div>

        <div className="flex flex-col gap-3 px-3 py-3 lg:py-0">
          {rubriques(entrees).map((rubrique) => (
            <section key={rubrique.intertitre}>
              <p
                id={rubrique.id}
                data-testid="intertitre"
                className="px-3 pb-1 text-xs font-semibold tracking-wide text-blanc-casse/60"
              >
                {rubrique.intertitre}
              </p>
              <ul
                aria-labelledby={rubrique.id}
                className="flex flex-col gap-1"
              >
                {rubrique.entrees
                  .filter((entree) => !entree.sous)
                  .map((entree) => {
                    // Les entrées « sous » celle-ci s'y emboîtent : la hiérarchie
                    // se lit dans la structure, pas seulement dans le retrait.
                    const dessous = rubrique.entrees.filter(
                      (autre) => autre.sous && autre.parent === entree.vers,
                    );
                    return (
                      <li key={entree.vers}>
                        <LienDeMenu entree={entree} />
                        {dessous.length > 0 ? (
                          <ul className="mt-1 flex flex-col gap-1">
                            {dessous.map((fille) => (
                              <li key={fille.vers}>
                                <LienDeMenu entree={fille} />
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    );
                  })}
              </ul>
            </section>
          ))}
        </div>

        <div className="mt-auto border-t border-white/10 px-3 py-3">
          {identite ? (
            <p className="px-3 pb-2 text-xs text-blanc-casse/70">
              {identite.prenom} {identite.nom}
              <span className="block text-blanc-casse/50">
                {identite.profils.map((profil) => profil.libelle).join(" · ")}
              </span>
            </p>
          ) : null}
          <Bouton
            variante="discret"
            taille="compact"
            className="w-full justify-start text-blanc-casse/80 hover:bg-anthracite-clair hover:text-blanc-casse"
            onClick={() => void deconnexion()}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Se déconnecter
          </Bouton>
        </div>
      </nav>

      <main className="flex-1 px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto w-full max-w-6xl">{children}</div>
      </main>
    </div>
  );
}

function LienDeMenu({ entree }: { entree: Entree }) {
  return (
    <NavLink
      to={entree.vers}
      end={entree.exact}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-[6px] px-3 py-2.5 text-left text-sm",
          entree.sous ? "ml-4" : null,
          isActive
            ? "bg-anthracite-clair text-chene"
            : "text-blanc-casse/80 hover:bg-anthracite-clair hover:text-blanc-casse",
        )
      }
    >
      {entree.icone}
      {entree.libelle}
    </NavLink>
  );
}

/** La recherche globale du §4 : un seul champ pour les trois familles. */
function ChampRecherche() {
  const naviguer = useNavigate();
  const [terme, setTerme] = useState("");

  return (
    <form
      role="search"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        const propre = terme.trim();
        if (propre.length >= 2) naviguer(`/recherche?q=${encodeURIComponent(propre)}`);
      }}
      className="relative"
    >
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blanc-casse/50"
        aria-hidden
      />
      <input
        type="search"
        value={terme}
        onChange={(evenement) => setTerme(evenement.target.value)}
        aria-label="Rechercher une Commande / Projet, un lot ou un client"
        placeholder="Rechercher…"
        className={cn(
          "h-10 w-full rounded-[6px] border border-white/15 bg-anthracite-clair pl-9 pr-3",
          "text-sm text-blanc-casse placeholder:text-blanc-casse/50",
        )}
      />
    </form>
  );
}
